// @ts-nocheck
/**
 * 大陸選択モジュール
 * 2枚のカードの属性から大陸を決定
 * @module twinKey/core/dungeon/continentSelector
 */

import { ATTRIBUTE_SCORES } from '../../data/constants';

// ============================================================
// 大陸選択閾値
// ============================================================

const CONTINENT_THRESHOLDS = {
  // 未来優先
  time_rift: { future: 10 },
  // 原始優先
  primal_depths: { heat: 8, darkness: 5, future: -10 },
  // 炎優勢
  aviculus: { heat: 10 },
  // 闇優勢
  black_curse: { darkness: 10 },
  // 炎+闇の混合
  steam_boundary: { heat: 5, darkness: 5 },
  // 光+闇の境界（負のスコア）
  twilight_realm: { heat: -5, darkness: -5 },
};

// ============================================================
// メイン関数
// ============================================================

/**
 * 2枚のカードから大陸を決定
 * @param {string} attr1 - カード1の属性
 * @param {string} attr2 - カード2の属性
 * @returns {string} 大陸ID
 */
export function selectContinent(attr1, attr2) {
  const total = calculateTotalScore(attr1, attr2);

  // 優先度順に判定
  // === 1. 未来優勢 → 時空の狭間 ===
  if (total.future >= 10) {
    return 'time_rift';
  }

  // === 2. 原始優勢（反未来 + 熱系）→ 原始の深淵 ===
  // 原始+何かで future が負になる場合
  if (total.future <= -5 && total.heat >= 5) {
    return 'primal_depths';
  }

  // === 3. 熱が強い → 巨鳥の島 ===
  if (total.heat >= 10) {
    return 'aviculus';
  }

  // === 4. 闇が強い → 黒呪の大陸 ===
  if (total.darkness >= 10) {
    return 'black_curse';
  }

  // === 5. 光+闇の均衡 → 黄昏の境界 ===
  // darkness が 0付近（光と闇が打ち消し合う: -2〜2）、heat が中程度（光成分あり: 3〜6）
  // 例: 光+闇 = heat:5, darkness:0 → twilight_realm
  if (total.darkness >= -2 && total.darkness <= 2 && total.heat >= 3 && total.heat <= 6) {
    return 'twilight_realm';
  }

  // === 6. 炎+水の混合 → 蒸気の境界 ===
  // heat と darkness が両方正で中程度
  // 例: 炎+水 = heat:5, darkness:3 → steam_boundary
  if (total.heat >= 3 && total.heat <= 6 && total.darkness >= 3 && total.darkness <= 5) {
    return 'steam_boundary';
  }

  // === 7. いずれにも該当しない → 未知の領域 ===
  return 'unknown';
}

/**
 * 2枚のカードの属性スコアを合計
 * @param {string} attr1 - カード1の属性
 * @param {string} attr2 - カード2の属性
 * @returns {{ heat: number, darkness: number, future: number }}
 */
export function calculateTotalScore(attr1, attr2) {
  const score1 = ATTRIBUTE_SCORES[attr1] || ATTRIBUTE_SCORES['なし'];
  const score2 = ATTRIBUTE_SCORES[attr2] || ATTRIBUTE_SCORES['なし'];

  return {
    heat: score1.heat + score2.heat,
    darkness: score1.darkness + score2.darkness,
    future: score1.future + score2.future,
  };
}

/**
 * 属性から主要な大陸を推測
 * UI表示用のヒント
 * @param {string} attribute - 属性
 * @returns {string[]} 関連する大陸ID配列
 */
export function getRelatedContinents(attribute) {
  const score = ATTRIBUTE_SCORES[attribute] || ATTRIBUTE_SCORES['なし'];
  const related = [];

  if (score.future >= 5) related.push('time_rift');
  if (score.heat >= 5) related.push('aviculus');
  if (score.darkness >= 5) related.push('black_curse');
  if (score.heat >= 3 && score.darkness >= 3) related.push('steam_boundary');
  if (score.heat < 0 && score.darkness < 0) related.push('twilight_realm');
  if (score.heat >= 5 && score.darkness >= 3 && score.future < 0) {
    related.push('primal_depths');
  }

  if (related.length === 0) {
    related.push('unknown');
  }

  return related;
}

/**
 * 2枚のカードの相性を計算
 * 同じ大陸に向かう場合はシナジーあり
 * @param {string} attr1 - カード1の属性
 * @param {string} attr2 - カード2の属性
 * @returns {{ synergy: number, description: string }}
 */
export function calculateAttributeSynergy(attr1, attr2) {
  const score1 = ATTRIBUTE_SCORES[attr1] || ATTRIBUTE_SCORES['なし'];
  const score2 = ATTRIBUTE_SCORES[attr2] || ATTRIBUTE_SCORES['なし'];

  // 同じ方向を向いているかチェック
  let synergy = 0;
  const descriptions = [];

  // 熱の相性
  if (score1.heat > 0 && score2.heat > 0) {
    synergy += 1;
    descriptions.push('炎の力が共鳴');
  } else if (score1.heat < 0 && score2.heat < 0) {
    synergy += 1;
    descriptions.push('冷気が響き合う');
  } else if ((score1.heat > 5 && score2.heat < -3) || (score1.heat < -3 && score2.heat > 5)) {
    synergy -= 1;
    descriptions.push('熱と冷が反発');
  }

  // 闇の相性
  if (score1.darkness > 0 && score2.darkness > 0) {
    synergy += 1;
    descriptions.push('闇の力が増幅');
  } else if (score1.darkness < 0 && score2.darkness < 0) {
    synergy += 1;
    descriptions.push('光が満ちる');
  }

  // 未来の相性
  if (score1.future > 0 && score2.future > 0) {
    synergy += 2;
    descriptions.push('時空が歪む');
  } else if ((score1.future > 5 && score2.future < -5) || (score1.future < -5 && score2.future > 5)) {
    synergy -= 1;
    descriptions.push('時の矛盾が生じる');
  }

  return {
    synergy,
    description: descriptions.join('、') || '特に相性なし',
  };
}
